/**
 * Role Form Modal Component
 * Part of TASK-334: Frontend Admin Panel
 *
 * Modal dialog for creating and editing roles with permission matrix.
 */

import React, { useState, useEffect } from 'react';
import { X, Shield } from 'lucide-react';
import type { Role, PermissionGroup, PermissionDefinitions } from '../../../types/admin';
import { roleService } from '../../../services/admin';
import PermissionMatrix from './PermissionMatrix';
import { Button } from '../../ui/button';

interface RoleFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; description?: string; permissions: string[]; is_default?: boolean }) => Promise<void>;
  role?: Role | null;
  isLoading?: boolean;
  isDuplicate?: boolean;
}

const RoleFormModal: React.FC<RoleFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  role,
  isLoading = false,
  isDuplicate = false,
}) => {
  const isEdit = !!role && !isDuplicate;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroup[]>([]);
  const [definitions, setDefinitions] = useState<PermissionDefinitions | null>(null);
  const [loadingDefinitions, setLoadingDefinitions] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load permission definitions
  useEffect(() => {
    if (isOpen && !definitions) {
      setLoadingDefinitions(true);
      roleService
        .getPermissionDefinitions()
        .then((defs) => {
          setDefinitions(defs);
        })
        .catch((err) => {
          console.error('Failed to load permissions:', err);
        })
        .finally(() => {
          setLoadingDefinitions(false);
        });
    }
  }, [isOpen, definitions]);

  // Reset form when modal opens/closes or role changes
  useEffect(() => {
    if (isOpen && definitions) {
      if (role) {
        setName(isDuplicate ? `${role.name} (Copy)` : role.name);
        setDescription(isDuplicate ? `Copy of ${role.name}` : role.description || '');
        setIsDefault(false);
        setPermissionGroups(
          roleService.transformToPermissionGroups(definitions, role.permissions)
        );
      } else {
        setName('');
        setDescription('');
        setIsDefault(false);
        setPermissionGroups(
          roleService.transformToPermissionGroups(definitions, [])
        );
      }
      setErrors({});
    }
  }, [isOpen, role, definitions, isDuplicate]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = 'Role name is required';
    } else if (name.length < 2) {
      newErrors.name = 'Role name must be at least 2 characters';
    } else if (name.length > 50) {
      newErrors.name = 'Role name must be less than 50 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const permissions = roleService.extractEnabledPermissions(permissionGroups);

    await onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      permissions,
      is_default: isDefault,
    });
  };

  const getSelectedCount = () => {
    return permissionGroups.reduce(
      (sum, group) => sum + group.permissions.filter((p) => p.enabled).length,
      0
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-2">
              <Shield size={20} className="text-[#002B45]" />
              <h2 className="text-lg font-semibold text-gray-900">
                {isDuplicate ? 'Duplicate Role' : isEdit ? 'Edit Role' : 'Create Role'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isEdit && role?.is_system_role}
                  className={`
                    w-full px-3 py-2 border rounded-lg text-sm
                    focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent
                    disabled:bg-gray-50 disabled:text-gray-500
                    ${errors.name ? 'border-red-300' : 'border-gray-300'}
                  `}
                  placeholder="e.g., Senior Advisor"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent resize-none"
                  placeholder="Describe what this role is for..."
                />
              </div>

              {/* Default role checkbox */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_default"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  className="rounded border-gray-300 text-[#002B45] focus:ring-[#F7B500]"
                />
                <label htmlFor="is_default" className="text-sm text-gray-700">
                  Set as default role for new users
                </label>
              </div>

              {/* Permissions */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Permissions
                  </label>
                  <span className="text-sm text-gray-500">
                    {getSelectedCount()} selected
                  </span>
                </div>

                {loadingDefinitions ? (
                  <div className="border border-gray-200 rounded-lg p-8 text-center">
                    <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-[#002B45] rounded-full mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Loading permissions...</p>
                  </div>
                ) : (
                  <PermissionMatrix
                    groups={permissionGroups}
                    onChange={setPermissionGroups}
                    disabled={isEdit && role?.is_system_role}
                  />
                )}

                {isEdit && role?.is_system_role && (
                  <p className="mt-2 text-sm text-amber-600">
                    System role permissions cannot be modified. Duplicate this role to create a custom version.
                  </p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end gap-3 flex-shrink-0 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading || (isEdit && role?.is_system_role)}
              >
                {isLoading
                  ? 'Saving...'
                  : isDuplicate
                  ? 'Create Copy'
                  : isEdit
                  ? 'Save Changes'
                  : 'Create Role'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RoleFormModal;
